import apiClient from "../../client/apiClient"
import { RECIPIENT_TYPES } from "../../constant"
import { appendQueryParams } from "../../utils"
import { Book, BookItem } from "../books"
import { Student, User } from "../student"

export type BookIssue = {
  _id: string
  book: Book
  bookItem: string
  recipient_type: (typeof RECIPIENT_TYPES)[keyof typeof RECIPIENT_TYPES]
  student: Student
  issue_date: Date
  due_date: Date
  status: string
  issued_by: User
  remarks: string
  perDayOverdueFine: number
  return_date: Date
  studentUser: User
  bookItemDoc: BookItem
  recipientUser: User
  recipientIdentifier: string
  enrollmentId?: string
}

export type CreateBookIssuePayload = {
  book: string
  bookItemId: string
  due_date: string
  issue_date: string
  perDayOverdueFine: number
  recipient_type: (typeof RECIPIENT_TYPES)[keyof typeof RECIPIENT_TYPES]
  remarks: string
  userId: string
}

export type ReturnBookData = {
  issue_id: string
  return_date: string
  remarks?: string
  /**
   * Legacy fine field (kept for backward compatibility)
   */
  fine_amount?: number
  /**
   * Book condition when returned (e.g. "same", "damaged", "misplaced")
   */
  conditionOnReturn?: string
  /**
   * Damage fine amount (legacy); prefer damageFinePercent
   */
  damageFineAmount?: number
  /**
   * Damage fine as percentage of book price (0–100); used when conditionOnReturn is "damaged"
   */
  damageFinePercent?: number
}

export type PaginationData = {
  currentPage: number
  hasNextPage: boolean
  hasPrevPage: boolean
  limit: number
  total: number
  totalPages: number
}

export type BookIssuesResponse = {
  success: boolean
  data: BookIssue[]
  pagination: PaginationData
}

export type BookIssueResponse = {
  success: boolean
  data: BookIssue
}

export type MessageResponse = {
  success: boolean
  message: string
  data?: BookIssue
}

export type ReactivateBookItemResponse = {
  success: boolean
  message: string
  data?: {
    _id: string
    status: string
    condition?: string
  }
}

const BOOK_ISSUE_ENDPOINT = {
  ISSUES: "/book/issues",
  ISSUE: "/book/issue",
  RETURN: "/book/returns",
}

export const getAllBookIssues = async ({
  page = 1,
  limit = 10,
  sortBy,
  sortOrder,
  searchText,
  student,
  book,
  status,
  fromDate,
  toDate,
  memberType,
}: {
  page?: number
  limit?: number
  sortBy?: string
  sortOrder?: string
  searchText?: string
  student?: string
  book?: string
  status?: string
  fromDate?: string
  toDate?: string
  memberType?: string
}): Promise<BookIssuesResponse> => {
  try {
    const { data: response } = await apiClient({
      url: appendQueryParams(BOOK_ISSUE_ENDPOINT.ISSUES, {
        page,
        limit,
        sortBy,
        sortOrder,
        searchText,
        student,
        book,
        status,
        fromDate,
        toDate,
        memberType,
      }),
      method: "GET",
    })

    if (response && response.success) {
      const pagination = response.pagination

      return {
        success: true,
        data: response.data,
        pagination: {
          total: pagination.total,
          totalPages: pagination.totalPages,
          currentPage: pagination.currentPage,
          limit: pagination.limit,
          hasNextPage: pagination.hasNextPage,
          hasPrevPage: pagination.hasPrevPage,
        },
      }
    }

    return {
      success: true,
      data: [],
      pagination: {
        total: 0,
        totalPages: 0,
        currentPage: page,
        limit: limit,
        hasNextPage: false,
        hasPrevPage: false,
      },
    }
  } catch (error) {
    console.error("Error in getAllBookIssues:", error)
    throw new Error("Failed to fetch book issues")
  }
}

// Issue a new book
export const issueBook = async (
  data: CreateBookIssuePayload,
): Promise<BookIssueResponse> => {
  const response = await apiClient({
    url: BOOK_ISSUE_ENDPOINT.ISSUE,
    method: "POST",
    data,
  })
  return response.data
}

// Return a book
export const returnBook = async (
  data: ReturnBookData,
): Promise<MessageResponse> => {
  const response = await apiClient({
    url: BOOK_ISSUE_ENDPOINT.RETURN,
    method: "POST",
    data,
  })
  return response.data
}

// Get book issue by ID
export const getBookIssueById = async (
  id: string,
): Promise<BookIssueResponse> => {
  const response = await apiClient({
    url: `${BOOK_ISSUE_ENDPOINT.ISSUES}/${id}`,
    method: "GET",
  })
  return response.data
}

// Reactivate a misplaced/lost book copy so it can be issued again
export const reactivateBookItemFromIssue = async (
  issueId: string,
  data?: { status?: string },
): Promise<ReactivateBookItemResponse> => {
  const response = await apiClient({
    url: `${BOOK_ISSUE_ENDPOINT.ISSUES}/${issueId}/reactivate`,
    method: "POST",
    data: data ?? {},
  })

  return response.data
}
